import { useTranslation } from 'react-i18next'
import { useEffect, useReducer } from 'react'
import useNotifications from '../../../hooks/useNotifications'
import { ctrSearch } from '../../../services/ordinanceService'
import { deepCopy } from '../../../util/Utils'
import { getCtr } from '../../../services/CtrService'
import { getIdElement } from '../../../util/StringUtil'

export default () => {}

const validCtrFactTypes = [
  'http://gedcomx.org/Birth',
  'http://gedcomx.org/Christening',
  'http://gedcomx.org/Marriage',
  'http://gedcomx.org/Death',
  'http://gedcomx.org/Burial',
]

export const dateFormats = {
  CTR_SEARCH_ENDPOINT: 'yyyy-MM-DD',
  DMCONSOLE: 'dd MMMM yyyy',
}

export function useCtrSearchState() {
  const { t } = useTranslation()
  const { error } = useNotifications()

  const initialState = {
    formMetadata: {
      ordinanceTypeOptions: [
        {
          value: 'BAPTISM',
          label: t('ordinances.types.baptism', 'Baptism'),
        },
        {
          value: 'CONFIRMATION',
          label: t('ordinances.types.confirmation', 'Confirmation'),
        },
        {
          value: 'INITIATORY',
          label: t('ordinances.types.initiatory', 'Initiatory'),
        },
        {
          value: 'ENDOWMENT',
          label: t('ordinances.types.endowment', 'Endowment'),
        },
        {
          value: 'SEALING_TO_PARENTS',
          label: t('ordinances.types.sealingToParents', 'Sealing to Parents'),
        },
        {
          value: 'SEALING_TO_SPOUSE',
          label: t('ordinances.types.sealingToSpouse', 'Sealing to Spouse'),
        },
        {
          value: 'LDS_LICENSE_MARRIAGE',
          label: t('ctr.ordinanceType.LDS_LICENSE_MARRIAGE', 'LDS License Marriage'),
        },
        {
          value: 'TIME_ONLY_MARRIAGE',
          label: t('ordinances.types.timeOnlyMarriage', 'Time-only Marriage'),
        },
        {
          value: 'MELCHIZEDEK_PRIESTHOOD_ORDINATION',
          label: t('ordinances.types.mechisedekPriesthoodOrdination', 'Melchizedek Priesthood Ordination'),
        },
      ],
      officialTypeOptions: [
        {
          value: 'OFFICIAL_PRIMARY',
          label: t('ctr.officialType.OFFICIAL_PRIMARY', 'Official Primary'),
        },
        {
          value: 'OFFICIAL_SECONDARY',
          label: t('ctr.officialType.OFFICIAL_SECONDARY', 'Official Secondary'),
        },
        {
          value: 'OFFICIAL_UNDETERMINED',
          label: t('ctr.officialType.OFFICIAL_UNDETERMINED', 'Official Undetermined'),
        },
        {
          value: 'UNOFFICIAL_PATRON',
          label: t('ctr.officialType.UNOFFICIAL_PATRON', 'Unofficial Patron'),
        },
        {
          value: 'OFFICIAL_RESTORATION_OF_BLESSINGS',
          label: t('ctr.officialType.OFFICIAL_RESTORATION_OF_BLESSINGS', 'Official Restoration Of Blessings'),
        },
      ],
      templeSelectedItem: { key: 'ctr search temple selected item', primaryText: '', templeCode: '' },
    },
    formState: {
      earliestPerformedDate: {
        key: 'earliestPerformedDate',
        primaryText: '',
      },
      latestPerformedDate: {
        key: 'latestPerformedDate',
        primaryText: '',
      },
      templeCode: '',
    },
    searchResults: null,
    ctrData: null,
  }

  const reducer = (_, currentState) => deepCopy(currentState)

  const [ctrSearchState, updateCtrSearchState] = useReducer(reducer, initialState)

  useEffect(() => {
    const fetchCtrs = async () => {
      const ctrResponses = await Promise.allSettled(
        ctrSearchState.searchResults.map((searchResult) => {
          return getCtr(searchResult.ctrId)
        })
      )
      const ctrDataFinal = {}
      ctrResponses.forEach((response) => {
        if (response.status !== 'rejected') {
          const principal = response.value.data.persons.find((person) => person.principal)
          const fact = principal?.facts?.find((f) => validCtrFactTypes.includes(f.type))
          ctrDataFinal[getIdElement(response.value.data.ctrs[0].id)] = fact
            ? {
                date: fact.date,
                place: fact.place,
                type: fact.type,
              }
            : null
        }
      })
      ctrSearchState.ctrData = ctrDataFinal
      updateCtrSearchState(ctrSearchState)
    }
    if (
      !ctrSearchState.ctrData &&
      ctrSearchState.searchResults &&
      ctrSearchState.searchResults.length > 0 &&
      ctrSearchState.searchResults.length <= 25
    ) {
      fetchCtrs()
    }
  }, [ctrSearchState, ctrSearchState.searchResults])

  const ctrSearchActions = {
    setName: (name) => {
      ctrSearchState.formState.name = name
      updateCtrSearchState(ctrSearchState)
    },

    setEarliestDate: (selectedItem) => {
      ctrSearchState.formState.earliestPerformedDate = selectedItem
      ctrSearchState.formState.latestPerformedDate = selectedItem
      updateCtrSearchState(ctrSearchState)
    },
    setLatestDate: (selectedItem) => {
      const dayDifference = selectedItem
        ? ctrSearchActions.getDayDifference(
            ctrSearchState.formState.earliestPerformedDate.formattedDate,
            selectedItem.formattedDate
          )
        : null
      if (dayDifference < 0) {
        error(
          t('ctr.search.errors.latestPerformedDateBeforeEarliestPerformedDate', {
            defaultValue: `Invalid Date: ${selectedItem?.primaryText}. Latest Performed Date can't be before Earliest Performed Date`,
            date: selectedItem?.primaryText,
          })
        )
      } else if (dayDifference > 7) {
        error(
          t('ctr.search.errors.latestPerformedDate1Week', {
            defaultValue: `Invalid Date: ${selectedItem?.primaryText}. Latest Performed Date must be within 1 week of Earliest Performed Date`,
            date: selectedItem?.primaryText,
          })
        )
      } else {
        ctrSearchState.formState.latestPerformedDate = selectedItem
      }
      updateCtrSearchState(ctrSearchState)
    },
    getDayDifference: (earliestDate, latestDate) => {
      const earliestPerformedDate = new Date(earliestDate)
      const latestPerformedDate = new Date(latestDate)
      const timeDifference = latestPerformedDate.getTime() - earliestPerformedDate.getTime()
      const dayDifference = Math.trunc(timeDifference / (1000 * 60 * 60 * 24))
      return dayDifference
    },
    getDateWarningMessage: (date) => {
      return date?.primaryText ? undefined : t('ctr.standardizedDateRequired', 'Standardized Date Required')
    },
    setOrdinanceType: (ordinanceType) => {
      ctrSearchState.formState.ordinanceType = ordinanceType
      updateCtrSearchState(ctrSearchState)
    },
    setTemple: (templeSelectedItem) => {
      setTempleState(ctrSearchState, templeSelectedItem)
      updateCtrSearchState(ctrSearchState)
    },
    clearTemple: () => {
      setTempleState(ctrSearchState, { key: 'ctr search temple selected item', primaryText: '', templeCode: '' })
      ctrSearchState.formState.latestPerformedDate = ctrSearchState.formState.earliestPerformedDate
      updateCtrSearchState(ctrSearchState)
    },
    setOfficialType: (officialType) => {
      ctrSearchState.formState.officialType = officialType
      updateCtrSearchState(ctrSearchState)
    },
    setSearchResults: (searchResults) => {
      ctrSearchState.searchResults = searchResults || []
      updateCtrSearchState(ctrSearchState)
    },
    reset: () => {
      setTempleState(ctrSearchState, { key: 'ctr search temple selected item', primaryText: '', templeCode: '' })
      ctrSearchState.formState = {
        earliestPerformedDate: {
          key: 'earliestPerformedDate',
          primaryText: '',
        },
        latestPerformedDate: {
          key: 'latestPerformedDate',
          primaryText: '',
        },
        templeCode: '',
      }
      ctrSearchState.searchResults = null
      ctrSearchState.ctrData = null
      updateCtrSearchState(ctrSearchState)
    },
    search: () => {
      ctrSearchState.ctrData = null
      ctrSearch(
        ctrSearchState.formState?.earliestPerformedDate?.formattedDate,
        ctrSearchState.formState?.latestPerformedDate?.formattedDate,
        ctrSearchState.formState.name.trim(),
        ctrSearchState.formState.officialType,
        ctrSearchState.formState.ordinanceType,
        ctrSearchState.formState.templeCode
      )
        .then((searchResults) => {
          ctrSearchActions.setSearchResults(searchResults)
        })
        .catch((err) => {
          if (err.response) {
            error(
              t('ctr.search.error.finishedWithError', {
                defaultValue: `Ctr Search Finished with error ${err.response.statusText}`,
                message: err.response.statusText,
              }),
              err
            )
          } else {
            error(
              t('ctr.search.error.finishedWithError', {
                defaultValue: `Ctr Search Finished with error ${err.message}`,
                message: err.message,
              }),
              err
            )
          }
        })
    },
  }

  // Helper Functions

  function setTempleState(newState, templeSelectedItem) {
    newState.formMetadata.templeSelectedItem = templeSelectedItem
    newState.formState.templeCode = templeSelectedItem.templeCode ? templeSelectedItem.templeCode : ''
  }

  return { ctrSearchState, ctrSearchActions }
}
